import { SelectItem, Select } from "@certn/cascadia-react";
import { slugify, slugifyPath } from "@certn/utils";
import { useContext, useEffect } from "react";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  controlledSelectKey,
  defaultSelectValueToCommit,
  selectValueForKey,
} from "./selectValue";
import {
  CascadiaOneOfProps,
  withCascadiaOneOfProps,
} from "../wrappers/withCascadiaOneOfProps";

export function SelectControl(props: CascadiaOneOfProps) {
  const { items, options, path } = props;

  const {
    isSummaryForPath,
    formID,
    isCheckResults,
    readonly,
    values: rootValues,
  } = useContext(CascadiaJsonFormContext);

  let limitedItems: typeof items | undefined;
  if (options.limitTo) {
    const { path, mapping } = options.limitTo;
    const otherValue =
      Array.isArray(path) &&
      path.reduce(
        (acc, key) => (acc && acc[key] ? acc[key] : undefined),
        rootValues
      );
    if (otherValue) {
      const limitedTo = mapping[otherValue];
      if (limitedTo) {
        limitedItems = items.filter((item) => limitedTo.includes(item.value));
      }
    }
  }

  const itemsToUse = limitedItems ?? items;
  const isSummary = Boolean(isSummaryForPath?.(path));
  const isEditable = Boolean(isCheckResults) && !readonly && !isSummary;

  useEffect(() => {
    if (itemsToUse.length === 1 && props.data !== itemsToUse[0].value) {
      setTimeout(() => {
        props.setValue(itemsToUse[0].value);
      });
    }
  }, [props.setValue, itemsToUse, props.data]);

  useEffect(() => {
    const defaultValue = defaultSelectValueToCommit({
      data: props.data,
      required: props.required,
      isEditable,
      schemaDefault: props.schema?.default,
      optionValues: itemsToUse.map((item) => item.value),
    });
    if (defaultValue !== undefined) {
      props.setValue(defaultValue);
    }
  }, [
    props.data,
    props.required,
    props.schema,
    props.setValue,
    isEditable,
    itemsToUse,
  ]);

  if (
    isSummaryForPath?.(path) &&
    // some fields are displayed as readonly text boxes rather than summary items
    !props.options?.renderSummaryAsReadOnly
  ) {
    const selectedOption = items.find((item) => item.value === props.data);

    return (
      <SummaryItem label={props.label}>
        {selectedOption?.label || props.data}
      </SummaryItem>
    );
  }

  return (
    <Select
      description={props.description}
      errors={props.errors}
      isDisabled={!props.enabled || isSummaryForPath?.(path)}
      isInvalid={props.invalid}
      isRequired={props.required}
      items={limitedItems ?? items}
      label={props.label}
      name={props.path}
      onBlur={props.setTouched}
      onSelectionChange={(key) => {
        // A null key is React Aria rejecting the selection. Writing it would
        // clear a required value that was never stored.
        if (key == null) {
          if (!props.required) {
            props.setValue(undefined);
          }
          return;
        }
        props.setValue(
          selectValueForKey(
            key,
            itemsToUse.map((item) => item.value)
          )
        );
      }}
      placeholder={options.placeholder}
      selectedKey={controlledSelectKey(props.data)}
      // testID={`check-engine-${slugify(props.label)}-select`}
      // changing to use path instead of label because label is likely to be translated
      testID={
        formID
          ? `${formID}-${slugifyPath(props.path)}-select`
          : `${slugifyPath(props.path)}-select`
      }
    >
      {(item) => (
        <SelectItem
          key={item.value}
          id={item.value}
          textValue={item.label}
          data-testid={
            formID
              ? `${formID}-${slugifyPath(props.path)}-select-item-${slugify(
                  item.value
                )}`
              : `${slugifyPath(props.path)}-select-item-${slugify(String(item.value))}`
          }
        >
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
}

SelectControl.displayName = "SelectControl";

export const SelectControlRenderer = withCascadiaOneOfProps(SelectControl);
